/**
 * Worker Scaling Config API Contract
 *
 * GET  /internal/infrastructure/worker-scaling-config
 * POST /internal/infrastructure/worker-scaling-config
 *
 * A system-wide DB override (SystemConfiguration: category=WORKER_SCALING,
 * key=CONFIG, schoolId=null) layered on top of the env/default base for the
 * worker auto-scaling system. Precedence: DB override > env var > code default.
 * Only fields present in the override apply; unset fields fall through.
 */

import { z } from 'zod'

/**
 * The effective, fully-resolved worker scaling configuration.
 * Mirrors the backend `WorkerScalingConfig` shape (the fields a client cares
 * about — PlanetScale/ECS coordinates are operational env and not exposed here).
 */
export const WorkerScalingConfigSchema = z.object({
  tiers: z.array(z.number()),
  minWorkers: z.number(),
  maxWorkers: z.number(),
  scaleUpThreshold: z.number(),
  scaleDownThreshold: z.number(),
  fastTrackThreshold: z.number(),
  sustainedChecks: z.number(),
  vtgateCpuLimit: z.number()
})

export type WorkerScalingConfigDto = z.infer<typeof WorkerScalingConfigSchema>

/**
 * The partial override that may be persisted in the DB. Every field is
 * optional; only present fields override the env/default base.
 */
export const WorkerScalingConfigOverrideSchema = z.object({
  maxWorkers: z.number().int().positive().optional(),
  minWorkers: z.number().int().positive().optional(),
  tiers: z.array(z.number().int().positive()).min(1).optional(),
  scaleUpThreshold: z.number().positive().optional(),
  scaleDownThreshold: z.number().positive().optional(),
  fastTrackThreshold: z.number().positive().optional(),
  sustainedChecks: z.number().int().positive().optional(),
  vtgateCpuLimit: z.number().positive().optional()
})

export type WorkerScalingConfigOverride = z.infer<typeof WorkerScalingConfigOverrideSchema>

/**
 * Audit metadata captured on every override write.
 */
export const WorkerScalingConfigAuditSchema = z.object({
  reason: z.string().nullable(),
  changedBy: z.string(),
  changedAt: z.string()
})

export type WorkerScalingConfigAudit = z.infer<typeof WorkerScalingConfigAuditSchema>

/**
 * GET response: effective merged config + the env/default base + the raw DB
 * override + the audit trail.
 */
export const GetWorkerScalingConfigResponseSchema = z.object({
  effective: WorkerScalingConfigSchema,
  base: WorkerScalingConfigSchema,
  override: WorkerScalingConfigOverrideSchema,
  audit: WorkerScalingConfigAuditSchema.nullable()
})

export type GetWorkerScalingConfigResponse = z.infer<typeof GetWorkerScalingConfigResponseSchema>

/**
 * POST request body: the override patch (all fields optional) plus optional
 * audit fields. `changed_by` is snake_case to match the internal-API body
 * convention used by sibling endpoints.
 */
export const SetWorkerScalingConfigRequestSchema = WorkerScalingConfigOverrideSchema.extend({
  reason: z.string().optional(),
  changed_by: z.string().optional()
})

export type SetWorkerScalingConfigRequest = z.infer<typeof SetWorkerScalingConfigRequestSchema>

/**
 * POST response: the merged effective config + the persisted override + audit.
 */
export const SetWorkerScalingConfigResponseSchema = z.object({
  effective: WorkerScalingConfigSchema,
  override: WorkerScalingConfigOverrideSchema,
  audit: WorkerScalingConfigAuditSchema
})

export type SetWorkerScalingConfigResponse = z.infer<typeof SetWorkerScalingConfigResponseSchema>
